/**
 * Data Conversion Module
 * Converts between document extract format, study extract format, and 2D arrays for AG Grid
 */

import { 
  getDisplayName,
  getCustomValue,
  getLevelDisplayName,
  getLevelRefs,
  findGrouperRow,
  ensureGrouperArray
} from './grouper-utils.js';

/**
 * Convert document extract column-based data to 2D array for AG Grid
 * @param tableData - Document extract table with column-based data
 * @returns 2D array with headers in first row
 */
export function documentTableTo2DArray(tableData: any): any[][] {
  if (!tableData?.data) return [];
  
  const columns = Object.keys(tableData.data);
  if (columns.length === 0) return [];
  
  // Get maximum row count
  const maxRows = Math.max(...columns.map(col => tableData.data[col]?.length || 0));
  
  // Build 2D array
  const result: any[][] = [];
  
  // Header row
  result.push(columns);
  
  // Data rows
  for (let i = 0; i < maxRows; i++) {
    const row: any[] = [];
    columns.forEach(col => {
      row.push(tableData.data[col][i] || '');
    });
    result.push(row);
  }
  
  return result;
}

/**
 * Convert working format table to 2D array for AG Grid
 * @param tableData - Working format table with current version data (target object)
 * @returns 2D array with headers in first row
 */
export function workingTableTo2DArray(tableData: any): any[][] {
  if (!tableData?.columns || !tableData?.rows) return [];
  
  const result: any[][] = [];
  
  // Header row - use column labels
  const headers = tableData.columns.map((col: any) => col.label);
  result.push(headers);
  
  // Data rows
  tableData.rows.forEach((row: any) => {
    const dataRow: any[] = [];
    tableData.columns.forEach((col: any) => {
      const cell = row.cells?.find((c: any) => c.id === col.id);
      dataRow.push(cell?.value || '');
    });
    result.push(dataRow);
  });
  
  return result;
}

/**
 * Convert document extract to 2D array for display in target grid
 * @param topicId - Topic ID (e.g., 'baseline', 'efficacy')
 * @param tableId - Table ID (e.g., 'age', 'gender')
 * @param documentExtract - The complete document extract data
 * @param projectStructure - The complete project structure
 * @returns 2D array with headers and data
 */
export function documentExtractTableTo2DArray(
  topicId: string,
  tableId: string,
  documentExtract: any,
  projectStructure: any
): any[][] {
  
  // Find topic in document extract
  const topicData = documentExtract.topics?.find((t: any) => t.id === topicId);
  if (!topicData) {
    return [];
  }
  
  // Find topic structure
  const topicStructure = projectStructure.topics?.find((t: any) => t.id === topicId);
  if (!topicStructure) {
    return [];
  }
  
  // Handle grouper table
  if (tableId === 'grouper') {
    return convertGrouperTo2DArray(topicData.grouper, topicStructure.grouper, documentExtract);
  }
  
  // Handle regular tables
  const tableStructure = topicStructure.tables?.find((t: any) => t.id === tableId);
  if (!tableStructure) {
    return [];
  }
  
  const tableData = topicData.tables?.find((t: any) => t.id === tableId);
  if (!tableData) {
    return [];
  }
  
  const result = convertTableTo2DArray(
    tableData,
    tableStructure,
    topicData.grouper,
    documentExtract,
    topicStructure.grouper
  );
  return result;
}

/**
 * Convert study extract to 2D array for display in target grid
 * @param topicId - Topic ID (e.g., 'baseline', 'efficacy')
 * @param tableId - Table ID (e.g., 'age', 'gender')
 * @param studyExtract - The complete study extract data
 * @param projectStructure - The complete project structure
 * @returns 2D array with headers and data
 */
export function studyTableTo2DArray(
  topicId: string,
  tableId: string,
  studyExtract: any,
  projectStructure: any
): any[][] {
  // Find topic in study extract
  const topicData = studyExtract.topics?.find((t: any) => t.id === topicId);
  if (!topicData) return [];
  
  // Find topic structure
  const topicStructure = projectStructure.topics?.find((t: any) => t.id === topicId);
  if (!topicStructure) return [];
  
  // Handle grouper table
  if (tableId === 'grouper') {
    return convertGrouperTo2DArray(topicData.grouper, topicStructure.grouper, studyExtract);
  }
  
  // Handle regular tables
  const tableStructure = topicStructure.tables?.find((t: any) => t.id === tableId);
  if (!tableStructure) return [];
  
  const tableData = topicData.tables?.find((t: any) => t.id === tableId);
  if (!tableData) return [];
  
  return convertTableTo2DArray(
    tableData,
    tableStructure,
    topicData.grouper,
    studyExtract,
    topicStructure.grouper
  );
}

/**
 * Convert grouper data to 2D array
 * Uses new format: ROW, STUDY, source columns
 */
function convertGrouperTo2DArray(grouperData: any, grouperStructure: any, studyExtract: any): any[][] {
  if (!grouperStructure?.levels) {
    throw new Error('[grouper] Grouper structure missing levels');
  }

  const rows = ensureGrouperArray(grouperData, 'convertGrouperTo2DArray');
  const studyName = getStudyName(studyExtract) || '-';

  const headers: string[] = ['ROW', 'STUDY'];
  const flattenedSources: Array<{
    levelNumber: string;
    source: any;
    referenceIndex?: number;
  }> = [];

  (grouperStructure.levels || []).forEach((level: any, levelIndex: number) => {
    const levelNumber = String(level.level ?? level.number ?? levelIndex + 1);
    let referenceCursor = 0;

    (level.sources || []).forEach((source: any) => {
      if (source.type === 'reference') {
        headers.push(source.refType || source.refTable || `Level ${levelNumber}`);
        flattenedSources.push({ levelNumber, source, referenceIndex: referenceCursor });
        referenceCursor += 1;
      } else if (source.type === 'custom') {
        headers.push('Custom');
        flattenedSources.push({ levelNumber, source });
      }
    });
  });

  const result: any[][] = [headers];

  rows.forEach((row: any, index: number) => {
    const dataRow: any[] = [];
    dataRow.push(String(row.row ?? index + 1));
    dataRow.push(studyName);

    flattenedSources.forEach(({ levelNumber, source, referenceIndex }) => {
      if (source.type === 'reference') {
        const refs = getLevelRefs(row, levelNumber);
        const refId = referenceIndex !== undefined ? refs[referenceIndex] || '' : '';
        if (refId) {
          dataRow.push(resolveReference(source.refTable, refId, studyExtract) || '-');
        } else {
          dataRow.push('-');
        }
      } else if (source.type === 'custom') {
        dataRow.push(getCustomValue(row) || '-');
      } else {
        dataRow.push('-');
      }
    });

    result.push(dataRow);
  });

  return result;
}

/**
 * Get source type from reference table name
 */
function getSourceTypeFromRef(refTable: string, levelStructure: any): string {
  if (!levelStructure?.sources) return '';
  
  const source = levelStructure.sources.find((s: any) => {
    if (s.type === 'reference' && s.refTable === refTable) {
      return true;
    }
    return false;
  });
  
  return source?.refType || '';
}

/**
 * Convert regular table data to 2D array
 */
function convertTableTo2DArray(
  tableData: any,
  tableStructure: any,
  grouperData: any,
  studyExtract: any,
  grouperStructure?: any
): any[][] {
  if (!tableData?.rows || !tableStructure?.columns) return [];
  
  const result: any[][] = [];
  
  // Build headers
  const headers = tableStructure.columns.map((col: any) => col.label || col.id);
  result.push(headers);
  
  // Build data rows
  tableData.rows.forEach((row: any, rowIndex: number) => {
    const dataRow: any[] = [];
    
    tableStructure.columns.forEach((column: any) => {
      // Handle grouper reference columns
      if (column.refTable === 'grouper' && column.refID) {
        if (!grouperStructure) {
          throw new Error('[grouper] Grouper structure required for table conversion');
        }

        if (!Array.isArray(grouperData)) {
          dataRow.push('');
          return;
        }

        const grouperRows = ensureGrouperArray(grouperData, 'convertTableTo2DArray');
        const grouperKey = row.key || row.id;

        let grouperRow: any = null;
        try {
          grouperRow = findGrouperRow(grouperRows, grouperKey, rowIndex);
        } catch (error) {
          logWarning('[grouper] Unable to resolve row for table conversion', "data-conversion", undefined, {
            tableId: tableStructure?.id,
            rowIndex,
            grouperKey,
            error
          });
        }

        if (!grouperRow) {
          dataRow.push('');
          return;
        }

        if (column.refID === 'name') {
          const targetLevelNumber = column.level !== undefined && column.level !== null
            ? String(column.level)
            : '1';

          const levelStructure = (grouperStructure.levels || []).find((level: any, idx: number) => {
            const identifier = String(level.level ?? level.number ?? idx + 1);
            return identifier === targetLevelNumber;
          });

          if (!levelStructure) {
            throw new Error(`[grouper] Level "${targetLevelNumber}" not defined in grouper structure`);
          }

          dataRow.push(getLevelDisplayName(grouperRow, levelStructure, studyExtract) || '');
        } else if (column.refID === 'custom' || column.refID === 'Custom') {
          dataRow.push(getCustomValue(grouperRow) || '');
        } else {
          dataRow.push('');
        }
      } else {
        // Handle regular cells - check if row has cells or direct properties
        if (row.cells) {
          const cell = row.cells.find((c: any) => c.id === column.id);
          dataRow.push(cell?.value || '');
        } else {
          // Direct property access (for document extract format)
          // Try multiple property name formats
          let value = '';
          
          // Try exact column ID
          if (row[column.id] !== undefined) {
            value = row[column.id] || '';
          }
          // Try lowercase with underscores
          else if (row[column.id.toLowerCase().replace(/\s+/g, '_')] !== undefined) {
            value = row[column.id.toLowerCase().replace(/\s+/g, '_')] || '';
          }
          // Try with question marks replaced
          else if (row[column.id.replace(/\?/g, '')] !== undefined) {
            value = row[column.id.replace(/\?/g, '')] || '';
          }
          // Try lowercase with underscores and no question marks
          else if (row[column.id.toLowerCase().replace(/\s+/g, '_').replace(/\?/g, '')] !== undefined) {
            value = row[column.id.toLowerCase().replace(/\s+/g, '_').replace(/\?/g, '')] || '';
          }
          
          dataRow.push(value);
        }
      }
    });
    
    result.push(dataRow);
  });
  
  return result;
}

/**
 * Resolve reference to actual value
 */
function resolveReference(refTable: string, refID: string, studyExtract: any): string {
  const collection = studyExtract.references?.find((ref: any) => ref.id === refTable);
  if (!collection) return refID;
  
  if (collection.rows) {
    const row = collection.rows.find((r: any) => r.id === refID);
    if (row?.cells) {
      const nameCell = row.cells.find((c: any) => c.id === 'name' || c.id === 'title');
      return nameCell?.value || refID;
    }
  }
  
  if (collection.values) {
    const value = collection.values.find((v: any) => v.id === refID);
    return value?.value || refID;
  }
  
  return refID;
}

/**
 * Convert 2D array back to study extract table format
 * @param data - 2D array with headers in first row
 * @param topicId - Topic ID
 * @param tableId - Table ID
 * @param projectStructure - Project structure for column definitions
 * @returns Study extract table format
 */
export function arrayToStudyTable(
  data: any[][],
  topicId: string,
  tableId: string,
  projectStructure: any,
  studyExtract: any
): any {
  if (!data || data.length === 0) return { rows: [] };
  
  // Find topic structure
  const topicStructure = projectStructure.topics?.find((t: any) => t.id === topicId);
  if (!topicStructure) return { rows: [] };
  
  // Find table structure
  const tableStructure = topicStructure.tables?.find((t: any) => t.id === tableId);
  if (!tableStructure) return { rows: [] };
  
  const headers = data[0];
  const dataRows = data.slice(1);
  
  // Create a map from column label/id to index in the 2D array headers
  const headerIndexMap = new Map<string, number>();
  headers.forEach((header: string, index: number) => {
    headerIndexMap.set(header, index);
  });
  
  // Get existing table data to preserve row IDs
  const topicData = studyExtract.topics?.find((t: any) => t.id === topicId);
  const existingTable = topicData?.tables?.find((t: any) => t.id === tableId);
  const existingRows = existingTable?.rows || [];
  
  // Create a map of existing rows by ID for better matching
  const existingRowsById = new Map<string, any>();
  existingRows.forEach((row: any) => {
    if (row.id) {
      existingRowsById.set(row.id, row);
    }
  });
  
  const rows = dataRows.map((rowData: any[], rowIndex: number) => {
    // Try to preserve existing row - match by index first
    let existingRow = existingRows[rowIndex];
    
    // Generate row ID - use existing if available, otherwise create new
    const rowId = existingRow?.id || `${tableId}_row_${rowIndex + 1}`;
    
    // If we didn't find by index but have a rowId, try to find by ID
    if (!existingRow && rowId && existingRowsById.has(rowId)) {
      existingRow = existingRowsById.get(rowId);
    }
    
    const cells: any[] = [];
    
    tableStructure.columns.forEach((column: any, colIndex: number) => {
      // Skip grouper reference columns (they're read-only and shouldn't be stored)
      if (column.refTable === 'grouper') {
        return;
      }
      
      // Find the correct index in rowData by matching column label/id to header
      const columnLabel = column.label || column.id;
      const dataIndex = headerIndexMap.get(columnLabel);
      const value = (dataIndex !== undefined && dataIndex >= 0) ? (rowData[dataIndex] || '') : '';
      
      // Preserve existing cell metadata if it exists - only look in the current row
      const existingCell = existingRow?.cells?.find((c: any) => c.id === column.id);
      
      // Build cell object, preserving all metadata properties if they exist
      const cell: any = {
        id: column.id,
        value: value
      };
      
      // Preserve sourceInfo if it exists - use deep copy to preserve all properties
      if (existingCell && 'sourceInfo' in existingCell && existingCell.sourceInfo !== undefined) {
        cell.sourceInfo = JSON.parse(JSON.stringify(existingCell.sourceInfo));
      }
      
      // Preserve reviewInfo if it exists (even if it's an empty object)
      if (existingCell && 'reviewInfo' in existingCell && existingCell.reviewInfo !== undefined) {
        cell.reviewInfo = JSON.parse(JSON.stringify(existingCell.reviewInfo));
      }
      
      // Note: confirmInfo is intentionally NOT preserved - only sourceInfo and reviewInfo are kept
      
      cells.push(cell);
    });
    
    return {
      id: rowId,
      cells: cells
    };
  });
  
  return { rows };
}

/**
 * Convert reference data to 2D array for display in target grid
 * @param referenceId - Reference ID (e.g., 'bibliography', 'treatment_arms')
 * @param extractData - The complete extract data (document or study)
 * @param projectStructure - The complete project structure
 * @returns 2D array with headers and data
 */
export function referenceTo2DArray(
  referenceId: string,
  extractData: any,
  projectStructure: any
): any[][] {
  
  // Find reference in extract data
  const referenceData = extractData.references?.find((r: any) => r.id === referenceId);
  if (!referenceData) {
    return [];
  }
  
  // Find reference structure
  const referenceStructure = projectStructure.references?.find((r: any) => r.id === referenceId);
  if (!referenceStructure) {
    return [];
  }
  
  // Handle object-type references (like study)
  if (referenceStructure.type === 'object' || referenceData.values) {
    // For object types, create vertical table format
    const values = referenceData.values || [];
    if (values.length === 0) return [];
    
    // Create 2D array: [["Field", "Value"], ...]
    const result: any[][] = [['Field', 'Value']];
    values.forEach((value: any) => {
      result.push([value.label || value.id || '', value.value || '']);
    });
    return result;
  }
  
  // Handle table-type references (like bibliography, treatment_arms)
  if (referenceStructure.type === 'table' || referenceData.rows) {
    const rows = referenceData.rows || [];
    
    // Get columns from structure - can be directly in referenceStructure or in referenceStructure.structure
    const columns = referenceStructure.columns || referenceStructure.structure?.columns || [];
    
    if (rows.length === 0) {
      // Return headers only if structure exists
      if (columns.length > 0) {
        const headers = columns.map((col: any) => col.label || col.id);
        return [headers];
      }
      return [];
    }
    let headers: string[] = [];
    
    if (columns.length > 0) {
      // Use structure columns - always prefer label over id for display
      headers = columns.map((col: any) => col.label || col.id);
    } else if (rows.length > 0 && rows[0].cells) {
      // Infer from first row cells
      headers = rows[0].cells.map((cell: any) => cell.id);
    } else {
      return [];
    }
    
    // Build 2D array
    const result: any[][] = [headers];
    
    rows.forEach((row: any) => {
      const dataRow: any[] = [];
      if (columns.length > 0) {
        // Use structure columns to map values - ensure all columns are represented
        columns.forEach((col: any) => {
          const cell = row.cells?.find((c: any) => c.id === col.id);
          // Always push a value - use empty string if cell doesn't exist
          dataRow.push(cell?.value ?? '');
        });
      } else {
        // Use cell order from first row (fallback if no structure)
        if (row.cells && row.cells.length > 0) {
          row.cells.forEach((cell: any) => {
            dataRow.push(cell.value ?? '');
          });
        }
      }
      result.push(dataRow);
    });
    
    return result;
  }
  
  return [];
}

